import { useQuery } from '@tanstack/react-query';
import { type UIMessage, validateUIMessages } from 'ai';
import { z } from 'zod';

/** One conversation as the server holds it. */
export interface Conversation {
  id: string;
  messages: UIMessage[];
  costUsd: number;
}

const conversationAnswer = z.object({
  conversation: z
    .object({ id: z.string(), messages: z.array(z.unknown()), costUsd: z.number() })
    .nullable(),
  maxUsd: z.number(),
});

const statusAnswer = z.object({ enabled: z.boolean() });

/** The server's answer to whether this person sees the assistant at all. */
export function useAssistantEnabled(signedIn: boolean): boolean {
  const status = useQuery({
    queryKey: ['assistant', 'status'],
    enabled: signedIn,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const response = await fetch('/assistant/status', { credentials: 'same-origin' });
      if (!response.ok) return { enabled: false };
      return statusAnswer.parse(await response.json());
    },
  });
  return status.data?.enabled === true;
}

/** The person's newest conversation, made on the spot when they have none. */
export async function fetchCurrentConversation(): Promise<Conversation & { maxUsd: number }> {
  const current = await readConversation(
    await fetch('/assistant/conversation', { credentials: 'same-origin' }),
  );
  return current.conversation === null ? startConversation() : current.conversation;
}

export async function startConversation(): Promise<Conversation & { maxUsd: number }> {
  const created = await readConversation(
    await fetch('/assistant/conversations', { method: 'POST', credentials: 'same-origin' }),
  );
  if (created.conversation === null) throw new Error('The server did not start a conversation.');
  return created.conversation;
}

async function readConversation(
  response: Response,
): Promise<{ conversation: (Conversation & { maxUsd: number }) | null }> {
  if (!response.ok) throw new Error(await errorOf(response));
  const answer = conversationAnswer.parse(await response.json());
  if (answer.conversation === null) return { conversation: null };
  const messages =
    answer.conversation.messages.length === 0
      ? []
      : await validateUIMessages({ messages: answer.conversation.messages });
  return {
    conversation: {
      id: answer.conversation.id,
      costUsd: answer.conversation.costUsd,
      messages,
      maxUsd: answer.maxUsd,
    },
  };
}

/** The server's own sentence, where it wrote one. */
export async function errorOf(response: Response): Promise<string> {
  const body = await response.text();
  try {
    const parsed = z.object({ error: z.string() }).safeParse(JSON.parse(body));
    if (parsed.success) return parsed.data.error;
  } catch {
    // Not JSON: the text itself is the best there is.
  }
  return body === '' ? `The server answered ${response.status}.` : body;
}
